import Papa from 'papaparse';
import * as XLSX from 'xlsx';

// Types for parsed import data
export interface ParsedProduct {
  row: number;
  name: string;
  sku: string;
  barcode?: string;
  price: number;
  cost?: number;
  tax_rate?: number;
  category?: string;
  description?: string;
  track_inventory?: boolean;
  low_stock_threshold?: number;
  par_level?: number;
  initial_stock?: number;
  modifier_groups?: string[];
}

export interface ParsedRawMaterial {
  row: number;
  name: string;
  sku?: string;
  barcode?: string;
  description?: string;
  unit_of_measure: string;
  cost_per_unit?: number;
  supplier_name?: string;
  supplier_item_code?: string;
  category?: string;
  reorder_point?: number;
  reorder_quantity?: number;
  initial_stock?: number;
}

export interface ParsedModifier {
  row: number;
  group_name: string;
  option_name: string;
  price_adjustment?: number;
  is_default?: boolean;
  min_selections?: number;
  max_selections?: number;
  is_required?: boolean;
  sort_order?: number;
}

export interface ParsedCategory {
  row: number;
  name: string;
  description?: string;
  parent_category?: string;
  sort_order?: number;
}

export interface ParsedImportData {
  products: ParsedProduct[];
  rawMaterials: ParsedRawMaterial[];
  modifiers: ParsedModifier[];
  categories: ParsedCategory[];
  parseErrors: ParseError[];
}

export interface ParseError {
  sheet: string;
  row: number;
  message: string;
}

// Column mappings for flexible header matching
const PRODUCT_COLUMN_MAP: Record<string, keyof ParsedProduct> = {
  'name': 'name',
  'product name': 'name',
  'product_name': 'name',
  'sku': 'sku',
  'product sku': 'sku',
  'barcode': 'barcode',
  'upc': 'barcode',
  'price': 'price',
  'selling price': 'price',
  'sell price': 'price',
  'cost': 'cost',
  'cost price': 'cost',
  'tax_rate': 'tax_rate',
  'tax rate': 'tax_rate',
  'tax': 'tax_rate',
  'category': 'category',
  'category name': 'category',
  'description': 'description',
  'track_inventory': 'track_inventory',
  'track inventory': 'track_inventory',
  'inventory': 'track_inventory',
  'low_stock_threshold': 'low_stock_threshold',
  'low stock threshold': 'low_stock_threshold',
  'reorder point': 'low_stock_threshold',
  'par_level': 'par_level',
  'par level': 'par_level',
  'initial_stock': 'initial_stock',
  'initial stock': 'initial_stock',
  'quantity': 'initial_stock',
  'qty': 'initial_stock',
  'modifier_groups': 'modifier_groups',
  'modifier groups': 'modifier_groups',
  'modifiers': 'modifier_groups',
};

const RAW_MATERIAL_COLUMN_MAP: Record<string, keyof ParsedRawMaterial> = {
  'name': 'name',
  'item name': 'name',
  'material name': 'name',
  'sku': 'sku',
  'barcode': 'barcode',
  'description': 'description',
  'unit_of_measure': 'unit_of_measure',
  'unit of measure': 'unit_of_measure',
  'unit': 'unit_of_measure',
  'uom': 'unit_of_measure',
  'cost_per_unit': 'cost_per_unit',
  'cost per unit': 'cost_per_unit',
  'cost': 'cost_per_unit',
  'unit cost': 'cost_per_unit',
  'supplier_name': 'supplier_name',
  'supplier name': 'supplier_name',
  'supplier': 'supplier_name',
  'vendor': 'supplier_name',
  'supplier_item_code': 'supplier_item_code',
  'supplier item code': 'supplier_item_code',
  'supplier sku': 'supplier_item_code',
  'vendor sku': 'supplier_item_code',
  'category': 'category',
  'type': 'category',
  'reorder_point': 'reorder_point',
  'reorder point': 'reorder_point',
  'reorder_quantity': 'reorder_quantity',
  'reorder quantity': 'reorder_quantity',
  'order qty': 'reorder_quantity',
  'initial_stock': 'initial_stock',
  'initial stock': 'initial_stock',
  'quantity': 'initial_stock',
  'qty': 'initial_stock',
  'on hand': 'initial_stock',
};

const MODIFIER_COLUMN_MAP: Record<string, keyof ParsedModifier> = {
  'group_name': 'group_name',
  'group name': 'group_name',
  'modifier group': 'group_name',
  'group': 'group_name',
  'option_name': 'option_name',
  'option name': 'option_name',
  'option': 'option_name',
  'modifier': 'option_name',
  'price_adjustment': 'price_adjustment',
  'price adjustment': 'price_adjustment',
  'price': 'price_adjustment',
  'adjustment': 'price_adjustment',
  'is_default': 'is_default',
  'is default': 'is_default',
  'default': 'is_default',
  'min_selections': 'min_selections',
  'min selections': 'min_selections',
  'min': 'min_selections',
  'minimum': 'min_selections',
  'max_selections': 'max_selections',
  'max selections': 'max_selections',
  'max': 'max_selections',
  'maximum': 'max_selections',
  'is_required': 'is_required',
  'is required': 'is_required',
  'required': 'is_required',
  'sort_order': 'sort_order',
  'sort order': 'sort_order',
  'order': 'sort_order',
};

const CATEGORY_COLUMN_MAP: Record<string, keyof ParsedCategory> = {
  'name': 'name',
  'category name': 'name',
  'category': 'name',
  'description': 'description',
  'parent_category': 'parent_category',
  'parent category': 'parent_category',
  'parent': 'parent_category',
  'sort_order': 'sort_order',
  'sort order': 'sort_order',
  'order': 'sort_order',
};

// Helper to normalize header names
function normalizeHeader(header: string): string {
  return header.toLowerCase().trim();
}

// Helper to parse boolean values
function parseBoolean(value: unknown): boolean | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'boolean') return value;
  const strValue = String(value).toLowerCase().trim();
  if (['true', 'yes', '1', 'y'].includes(strValue)) return true;
  if (['false', 'no', '0', 'n'].includes(strValue)) return false;
  return undefined;
}

// Helper to parse number values
function parseNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const num = Number(value);
  return isNaN(num) ? undefined : num;
}

// Helper to parse string values
function parseString(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  return String(value).trim();
}

// Map headers to standardized field names
function mapHeaders<T>(
  headers: string[],
  columnMap: Record<string, keyof T>
): Map<number, keyof T> {
  const mapping = new Map<number, keyof T>();
  headers.forEach((header, index) => {
    const normalized = normalizeHeader(header);
    const field = columnMap[normalized];
    if (field) {
      mapping.set(index, field);
    }
  });
  return mapping;
}

// Parse a single product row
function parseProductRow(
  row: unknown[],
  headerMap: Map<number, keyof ParsedProduct>,
  rowNumber: number
): ParsedProduct | null {
  const product: Partial<ParsedProduct> = { row: rowNumber };

  headerMap.forEach((field, index) => {
    const value = row[index];
    switch (field) {
      case 'name':
      case 'sku':
      case 'barcode':
      case 'category':
      case 'description':
        product[field] = parseString(value);
        break;
      case 'price':
      case 'cost':
      case 'tax_rate':
      case 'low_stock_threshold':
      case 'par_level':
      case 'initial_stock':
        product[field] = parseNumber(value);
        break;
      case 'track_inventory':
        product[field] = parseBoolean(value);
        break;
      case 'modifier_groups':
        const strVal = parseString(value);
        if (strVal) {
          product[field] = strVal.split(';').map(s => s.trim()).filter(Boolean);
        }
        break;
    }
  });

  // Skip empty rows
  if (!product.name && !product.sku) return null;

  return product as ParsedProduct;
}

// Parse a single raw material row
function parseRawMaterialRow(
  row: unknown[],
  headerMap: Map<number, keyof ParsedRawMaterial>,
  rowNumber: number
): ParsedRawMaterial | null {
  const material: Partial<ParsedRawMaterial> = { row: rowNumber };

  headerMap.forEach((field, index) => {
    const value = row[index];
    switch (field) {
      case 'name':
      case 'sku':
      case 'barcode':
      case 'description':
      case 'unit_of_measure':
      case 'supplier_name':
      case 'supplier_item_code':
      case 'category':
        material[field] = parseString(value);
        break;
      case 'cost_per_unit':
      case 'reorder_point':
      case 'reorder_quantity':
      case 'initial_stock':
        material[field] = parseNumber(value);
        break;
    }
  });

  // Skip empty rows
  if (!material.name) return null;

  return material as ParsedRawMaterial;
}

// Parse a single modifier row
function parseModifierRow(
  row: unknown[],
  headerMap: Map<number, keyof ParsedModifier>,
  rowNumber: number
): ParsedModifier | null {
  const modifier: Partial<ParsedModifier> = { row: rowNumber };

  headerMap.forEach((field, index) => {
    const value = row[index];
    switch (field) {
      case 'group_name':
      case 'option_name':
        modifier[field] = parseString(value);
        break;
      case 'price_adjustment':
      case 'min_selections':
      case 'max_selections':
      case 'sort_order':
        modifier[field] = parseNumber(value);
        break;
      case 'is_default':
      case 'is_required':
        modifier[field] = parseBoolean(value);
        break;
    }
  });

  // Skip empty rows
  if (!modifier.group_name && !modifier.option_name) return null;

  return modifier as ParsedModifier;
}

// Parse a single category row
function parseCategoryRow(
  row: unknown[],
  headerMap: Map<number, keyof ParsedCategory>,
  rowNumber: number
): ParsedCategory | null {
  const category: Partial<ParsedCategory> = { row: rowNumber };

  headerMap.forEach((field, index) => {
    const value = row[index];
    switch (field) {
      case 'name':
      case 'description':
      case 'parent_category':
        category[field] = parseString(value);
        break;
      case 'sort_order':
        category[field] = parseNumber(value);
        break;
    }
  });

  // Skip empty rows
  if (!category.name) return null;

  return category as ParsedCategory;
}

// Detect sheet type based on headers
function detectSheetType(headers: string[]): 'products' | 'rawMaterials' | 'modifiers' | 'categories' | 'unknown' {
  const normalizedHeaders = headers.map(normalizeHeader);

  // Check for modifier-specific headers
  if (normalizedHeaders.some(h =>
    h.includes('group_name') || h.includes('group name') ||
    h.includes('option_name') || h.includes('option name') ||
    h.includes('modifier group')
  )) {
    return 'modifiers';
  }

  // Check for raw material-specific headers
  if (normalizedHeaders.some(h =>
    h.includes('unit_of_measure') || h.includes('unit of measure') ||
    h.includes('uom') || h.includes('supplier_name') || h.includes('supplier name') ||
    h.includes('supplier_item_code')
  )) {
    return 'rawMaterials';
  }

  // Check for category-specific headers (minimal set)
  if (normalizedHeaders.length <= 4 &&
      normalizedHeaders.some(h => h === 'name' || h === 'category' || h === 'category name') &&
      normalizedHeaders.some(h => h.includes('parent') || h === 'description')) {
    return 'categories';
  }

  // Check for product headers
  if (normalizedHeaders.some(h =>
    h === 'price' || h === 'selling price' || h === 'sku' ||
    h === 'product name' || h === 'barcode'
  )) {
    return 'products';
  }

  return 'unknown';
}

// Parse CSV file
export async function parseCSV(file: File): Promise<ParsedImportData> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as unknown[][];
        if (data.length < 2) {
          resolve({
            products: [],
            rawMaterials: [],
            modifiers: [],
            categories: [],
            parseErrors: [{ sheet: 'CSV', row: 0, message: 'File is empty or has no data rows' }],
          });
          return;
        }

        const headers = (data[0] as string[]).map(String);
        const sheetType = detectSheetType(headers);
        const result: ParsedImportData = {
          products: [],
          rawMaterials: [],
          modifiers: [],
          categories: [],
          parseErrors: [],
        };

        switch (sheetType) {
          case 'products': {
            const headerMap = mapHeaders(headers, PRODUCT_COLUMN_MAP);
            for (let i = 1; i < data.length; i++) {
              const parsed = parseProductRow(data[i], headerMap, i + 1);
              if (parsed) result.products.push(parsed);
            }
            break;
          }
          case 'rawMaterials': {
            const headerMap = mapHeaders(headers, RAW_MATERIAL_COLUMN_MAP);
            for (let i = 1; i < data.length; i++) {
              const parsed = parseRawMaterialRow(data[i], headerMap, i + 1);
              if (parsed) result.rawMaterials.push(parsed);
            }
            break;
          }
          case 'modifiers': {
            const headerMap = mapHeaders(headers, MODIFIER_COLUMN_MAP);
            for (let i = 1; i < data.length; i++) {
              const parsed = parseModifierRow(data[i], headerMap, i + 1);
              if (parsed) result.modifiers.push(parsed);
            }
            break;
          }
          case 'categories': {
            const headerMap = mapHeaders(headers, CATEGORY_COLUMN_MAP);
            for (let i = 1; i < data.length; i++) {
              const parsed = parseCategoryRow(data[i], headerMap, i + 1);
              if (parsed) result.categories.push(parsed);
            }
            break;
          }
          default:
            result.parseErrors.push({
              sheet: 'CSV',
              row: 1,
              message: 'Could not determine data type from headers. Please use the provided template.',
            });
        }

        resolve(result);
      },
      error: (error) => {
        reject(new Error(`CSV parsing error: ${error.message}`));
      },
    });
  });
}

// Parse Excel file
export async function parseExcel(file: File): Promise<ParsedImportData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        const result: ParsedImportData = {
          products: [],
          rawMaterials: [],
          modifiers: [],
          categories: [],
          parseErrors: [],
        };

        // Process each sheet
        for (const sheetName of workbook.SheetNames) {
          // Skip instructions sheet
          if (sheetName.toLowerCase() === 'instructions') continue;

          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as unknown[][];

          if (jsonData.length < 2) continue;

          const headers = (jsonData[0] as string[]).map(String);
          const sheetLower = sheetName.toLowerCase();

          // Determine sheet type by name first, then by headers
          let sheetType: 'products' | 'rawMaterials' | 'modifiers' | 'categories' | 'unknown';

          if (sheetLower.includes('product')) {
            sheetType = 'products';
          } else if (sheetLower.includes('raw') || sheetLower.includes('material') || sheetLower.includes('ingredient')) {
            sheetType = 'rawMaterials';
          } else if (sheetLower.includes('modifier')) {
            sheetType = 'modifiers';
          } else if (sheetLower.includes('categor')) {
            sheetType = 'categories';
          } else {
            sheetType = detectSheetType(headers);
          }

          switch (sheetType) {
            case 'products': {
              const headerMap = mapHeaders(headers, PRODUCT_COLUMN_MAP);
              for (let i = 1; i < jsonData.length; i++) {
                const parsed = parseProductRow(jsonData[i], headerMap, i + 1);
                if (parsed) result.products.push(parsed);
              }
              break;
            }
            case 'rawMaterials': {
              const headerMap = mapHeaders(headers, RAW_MATERIAL_COLUMN_MAP);
              for (let i = 1; i < jsonData.length; i++) {
                const parsed = parseRawMaterialRow(jsonData[i], headerMap, i + 1);
                if (parsed) result.rawMaterials.push(parsed);
              }
              break;
            }
            case 'modifiers': {
              const headerMap = mapHeaders(headers, MODIFIER_COLUMN_MAP);
              for (let i = 1; i < jsonData.length; i++) {
                const parsed = parseModifierRow(jsonData[i], headerMap, i + 1);
                if (parsed) result.modifiers.push(parsed);
              }
              break;
            }
            case 'categories': {
              const headerMap = mapHeaders(headers, CATEGORY_COLUMN_MAP);
              for (let i = 1; i < jsonData.length; i++) {
                const parsed = parseCategoryRow(jsonData[i], headerMap, i + 1);
                if (parsed) result.categories.push(parsed);
              }
              break;
            }
            default:
              result.parseErrors.push({
                sheet: sheetName,
                row: 1,
                message: `Could not determine data type for sheet "${sheetName}". Skipping.`,
              });
          }
        }

        resolve(result);
      } catch (error) {
        reject(new Error(`Excel parsing error: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(file);
  });
}

// Main parse function that detects file type
export async function parseImportFile(file: File): Promise<ParsedImportData> {
  const fileName = file.name.toLowerCase();

  if (fileName.endsWith('.csv')) {
    return parseCSV(file);
  } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
    return parseExcel(file);
  } else {
    throw new Error('Unsupported file type. Please use CSV or Excel (.xlsx, .xls) files.');
  }
}

// Get file extension
export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
}

// Check if file type is supported
export function isSupportedFileType(filename: string): boolean {
  const ext = getFileExtension(filename);
  return ['csv', 'xlsx', 'xls'].includes(ext);
}
